// Constructor.io behavioral beacon helpers, mirroring mb3-frontend's
// src/lib/analytics/utils/constructorBeacon.ts. The beacon script auto-collects
// views/clicks from `data-cnstrc-*` attributes; these builders keep the attribute
// vocabulary consistent with mb3 so both apps feed pods the same signal shapes.

type ConstructorAttributeValue = string | number | boolean | null | undefined

export type ConstructorTrackingAttributes = Record<
  `data-cnstrc-${string}`,
  string
>

// Keep in sync with CONSTRUCTOR_POD_IDS in packages/mtag-bff/src/config.ts —
// that copy is server-only and can't be imported into client bundles.
export const CONSTRUCTOR_POD_IDS = {
  findSimilar: 'mtag-find-similar',
  pairsWith: 'mtag-pairs-with',
} as const

export function constructorTrackingAttributes(
  attributes: Record<`data-cnstrc-${string}`, ConstructorAttributeValue>,
): ConstructorTrackingAttributes {
  return Object.entries(attributes).reduce<ConstructorTrackingAttributes>(
    (accumulator, [key, value]) => {
      if (value === null || value === undefined || value === false) {
        return accumulator
      }

      if (value === true) {
        accumulator[key as `data-cnstrc-${string}`] = ''
        return accumulator
      }

      const stringValue = String(value)

      if (!stringValue) {
        return accumulator
      }

      accumulator[key as `data-cnstrc-${string}`] = stringValue
      return accumulator
    },
    {},
  )
}

// The beacon config script (rendered in the root layout, above the auth providers) can't see
// the session, so the signed-in user id is attached here once auth resolves on the client —
// mirroring mb3, which sets window.cnstrc.userId so recommendation views/clicks tie to the user.
type ConstructorWindow = { cnstrc?: Record<string, unknown> }

export function setConstructorBeaconUserId(userId: string): void {
  if (typeof window === 'undefined') {
    return
  }

  const w = window as unknown as ConstructorWindow
  w.cnstrc = w.cnstrc || {}
  w.cnstrc.userId = userId
}

export function clearConstructorBeaconUserId(): void {
  if (typeof window === 'undefined') {
    return
  }

  const w = window as unknown as ConstructorWindow

  if (w.cnstrc) {
    delete w.cnstrc.userId
  }
}

export function constructorProductDetailAttributes({
  itemId,
  itemName,
  variationId,
}: {
  itemId?: string
  itemName?: string
  variationId?: string
}): ConstructorTrackingAttributes {
  return constructorTrackingAttributes({
    'data-cnstrc-product-detail': true,
    'data-cnstrc-item-id': itemId,
    'data-cnstrc-item-name': itemName,
    'data-cnstrc-item-variation-id': variationId,
  })
}

export function constructorRecommendationListAttributes({
  podId,
  totalResults,
  seedItems,
}: {
  podId: string
  totalResults: number
  seedItems?: string[]
}): ConstructorTrackingAttributes {
  return constructorTrackingAttributes({
    'data-cnstrc-recommendations': true,
    'data-cnstrc-recommendations-pod-id': podId,
    'data-cnstrc-num-results': totalResults,
    'data-cnstrc-recommendations-seed-items': seedItems
      ?.filter(Boolean)
      .join(','),
  })
}

export function constructorRecommendationItemAttributes({
  itemId,
  itemName,
  variationId,
}: {
  itemId?: string
  itemName?: string
  variationId?: string
}): ConstructorTrackingAttributes {
  return constructorTrackingAttributes({
    'data-cnstrc-item': 'recommendation',
    'data-cnstrc-item-id': itemId,
    'data-cnstrc-item-name': itemName,
    'data-cnstrc-item-variation-id': variationId,
  })
}
